import type { SocialLoginChannel } from "@/lib/settings/basic-page";

export const SOCIAL_PROVIDERS = ["wechat", "qq", "douyin"] as const;
export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number];
export const SOCIAL_LOGIN_TYPE_OPTIONS = [
  { value: "disabled", label: "不启用" },
  { value: "member", label: "只前端使用" },
  { value: "admin", label: "只后端使用" },
  { value: "both", label: "前后端使用" },
] as const;
export function resolveSocialLoginType(channel: Pick<SocialLoginChannel, "enabled" | "adminEnabled">) {
  return channel.enabled ? (channel.adminEnabled ? "both" : "member") : (channel.adminEnabled ? "admin" : "disabled");
}
export function socialLoginTypeLabel(channel: Pick<SocialLoginChannel, "enabled" | "adminEnabled">) {
  return SOCIAL_LOGIN_TYPE_OPTIONS.find(option => option.value === resolveSocialLoginType(channel))!.label;
}
export const SOCIAL_PROVIDER_LABELS: Record<SocialProvider, string> = {
  wechat: "微信网站扫码", qq: "QQ互联", douyin: "抖音网站扫码",
};
export const SOCIAL_CREDENTIAL_LABELS: Record<SocialProvider, { appId: string; appSecret: string }> = {
  wechat: { appId: "AppID", appSecret: "AppSecret" },
  qq: { appId: "APP ID", appSecret: "APP Key" },
  douyin: { appId: "Client Key", appSecret: "Client Secret" },
};
const DEFAULT_PROVIDERS: Record<string, SocialProvider> = {
  wechatlogin: "wechat", qqlogin: "qq", douyinlogin: "douyin",
};
export function resolveSocialProvider(channel: { id: string; provider?: unknown }): SocialProvider | undefined {
  if (channel.provider !== undefined) return SOCIAL_PROVIDERS.includes(channel.provider as SocialProvider) ? channel.provider as SocialProvider : undefined;
  return DEFAULT_PROVIDERS[channel.id];
}
export function isSupportedSocialProvider(provider: unknown): provider is SocialProvider {
  return SOCIAL_PROVIDERS.includes(provider as SocialProvider);
}
export function socialCredentials(channel: SocialLoginChannel) {
  const read = (keys: string[]) => channel.config.find((item) => keys.includes(item.key.toLowerCase().replace(/[_\s-]/g, "")))?.value.trim() ?? "";
  return { appId: read(["appid", "clientid", "clientkey"]), appSecret: read(["appsecret", "appkey", "clientsecret"]), redirectUri: read(["redirecturi", "redirecturl", "callbackurl"]) };
}
export function socialConfigReady(channel: SocialLoginChannel) {
  const credentials = socialCredentials(channel);
  return isSupportedSocialProvider(resolveSocialProvider(channel)) && /^[a-zA-Z0-9_-]{1,128}$/.test(credentials.appId) && Boolean(credentials.appSecret);
}

export const SOCIAL_CHANNEL_ID_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;
export function socialCallbackPath(id: string) {
  return `/api/auth/member/social/${encodeURIComponent(id)}/callback`;
}
export function socialStartHref(id: string, next?: string | null, bind = false) {
  const query = new URLSearchParams();
  if (next) query.set("next", next);
  if (bind) query.set("mode", "bind");
  return `/api/auth/member/social/${encodeURIComponent(id)}/start${query.size ? `?${query}` : ""}`;
}
export type PublicSocialChannel = { id: string; name: string; provider?: SocialProvider; bound?: boolean; enabled?: boolean; bindingId?: string; wechatOfficialAccount?: boolean };

export const SOCIAL_ERROR_CODES = ["UNSUPPORTED_PROVIDER", "PROVIDER_FAILED", "CHANNEL_UNAVAILABLE", "CHANNEL_CHANGED", "SOCIAL_EXPIRED", "IDENTITY_CONFLICT", "REAUTH_REQUIRED", "ACCOUNT_MISMATCH", "ALREADY_BOUND", "ACCOUNT_UNAVAILABLE", "INVALID_STATE", "CALLBACK_MISMATCH", "AUTH_CANCELLED", "BAD_REQUEST", "RATE_LIMITED", "PENDING_CHANGED", "PENDING_CONFLICT", "PENDING_LIMIT", "INVALID_PASSWORD", "LAST_LOGIN_METHOD", "BINDING_CHANGED", "REGISTRATION_CLOSED", "INTERNAL_ERROR"] as const;
export type SocialErrorCode = (typeof SOCIAL_ERROR_CODES)[number];
export function isSocialErrorCode(code: unknown): code is SocialErrorCode { return typeof code === "string" && SOCIAL_ERROR_CODES.includes(code as SocialErrorCode); }
